import { useState, useRef, useCallback, useEffect } from 'react';
import { uploadSingleFile, type UploadedFileRef } from '@/api/campaigns';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface FileUploadStatus {
  filename: string;
  status: 'pending' | 'uploading' | 'done' | 'failed';
  progress: number;
  error?: string;
}

export interface UploadFileEntry {
  file: File;
  ratio?: string;
  assetTypeId?: string;
  existingAssetId?: string;
}

const UPLOAD_CONCURRENCY = 2;

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useUploadProgress() {
  const [uploadProgress, setUploadProgress] = useState<FileUploadStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Abort any in-flight uploads if the component unmounts (e.g. user navigates away)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const uploadFiles = useCallback(
    async (
      campaignId: string,
      files: UploadFileEntry[],
    ): Promise<{
      refs: UploadedFileRef[];
      failures: { filename: string; error: string; index: number }[];
    }> => {
      if (files.length === 0) return { refs: [], failures: [] };

      const refsArray: (UploadedFileRef | null)[] = new Array(files.length).fill(null);
      const failures: { filename: string; error: string; index: number }[] = [];

      // Create a fresh AbortController for this batch
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Initialize progress state and show modal
      setIsUploading(true);
      setShowUploadProgress(true);
      setUploadProgress(
        files.map((f) => ({
          filename: f.file.name,
          status: 'pending',
          progress: 0,
        })),
      );

      let fileIndex = 0;

      const processNext = async (): Promise<void> => {
        if (fileIndex >= files.length) return;

        const currentIndex = fileIndex++;
        const { file, ratio, assetTypeId, existingAssetId } = files[currentIndex];

        setUploadProgress((prev) =>
          prev.map((p, i) => (i === currentIndex ? { ...p, status: 'uploading' } : p)),
        );

        try {
          const result = await uploadSingleFile(campaignId, file, {
            ratio,
            assetTypeId,
            existingAssetId,
            signal: abortController.signal,
            onProgress: (percent) => {
              setUploadProgress((prev) =>
                prev.map((p, i) => (i === currentIndex ? { ...p, progress: percent } : p)),
              );
            },
          });

          refsArray[currentIndex] = result;

          setUploadProgress((prev) =>
            prev.map((p, i) => (i === currentIndex ? { ...p, status: 'done', progress: 100 } : p)),
          );
        } catch (err: unknown) {
          const isAbort =
            (err instanceof DOMException && err.name === 'AbortError') ||
            (err &&
              typeof err === 'object' &&
              'code' in err &&
              (err as { code?: string }).code === 'ERR_CANCELED');

          if (isAbort) {
            setUploadProgress((prev) =>
              prev.map((p, i) =>
                i >= currentIndex && p.status !== 'done'
                  ? { ...p, status: 'failed', error: 'Cancelled' }
                  : p,
              ),
            );
            fileIndex = files.length;
            return;
          }

          let errorMessage = 'Upload failed';
          if (err && typeof err === 'object' && 'response' in err) {
            const axiosError = err as {
              response?: { data?: { detail?: string } };
              message?: string;
            };
            if (axiosError.response?.data?.detail) {
              errorMessage = axiosError.response.data.detail;
            } else if (axiosError.message) {
              errorMessage = axiosError.message;
            }
          } else if (err instanceof Error) {
            errorMessage = err.message;
          }

          failures.push({ filename: file.name, error: errorMessage, index: currentIndex });

          setUploadProgress((prev) =>
            prev.map((p, i) =>
              i === currentIndex ? { ...p, status: 'failed', error: errorMessage } : p,
            ),
          );
        }
      };

      const worker = async () => {
        while (fileIndex < files.length) {
          await processNext();
        }
      };

      const uploadPromises: Promise<void>[] = [];
      for (let i = 0; i < Math.min(UPLOAD_CONCURRENCY, files.length); i++) {
        uploadPromises.push(worker());
      }
      await Promise.all(uploadPromises);

      setIsUploading(false);
      abortControllerRef.current = null;

      const refs = refsArray.filter((r): r is UploadedFileRef => r !== null);
      return { refs, failures };
    },
    [],
  );

  const dismiss = useCallback(() => {
    setShowUploadProgress(false);
    setUploadProgress([]);
  }, []);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  return { uploadFiles, uploadProgress, isUploading, showUploadProgress, dismiss, cancel };
}

// ── Modal Component ────────────────────────────────────────────────────────────

interface UploadProgressModalProps {
  uploadProgress: FileUploadStatus[];
  isUploading: boolean;
  show: boolean;
  onCancel: () => void;
  onDismiss: () => void;
  /**
   * True while a longer operation (validation) is still running *after* the
   * uploads themselves have finished. The modal stays open for that whole time,
   * so the close (X) is hidden and replaced with a spinner. A greyed-out X still
   * reads as "close" and invites a dead click that would only hide the modal
   * mid-validation; a spinner says "wait" instead.
   */
  busy?: boolean;
}

export function UploadProgressModal({
  uploadProgress,
  isUploading,
  show,
  onCancel,
  onDismiss,
  busy = false,
}: UploadProgressModalProps) {
  if (!show || uploadProgress.length === 0) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 z-[80]" />

      {/* Progress Modal */}
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upload-progress-title"
          aria-describedby="upload-progress-description"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 id="upload-progress-title" className="text-lg font-semibold text-gray-900">
              {isUploading
                ? 'Uploading Files...'
                : uploadProgress.some((f) => f.status === 'failed')
                  ? 'Upload Failed'
                  : busy
                    ? 'Validating...'
                    : 'Upload Complete'}
            </h3>
            <p id="upload-progress-description" className="sr-only">
              {isUploading
                ? `Uploading ${uploadProgress.filter((f) => f.status === 'done').length} of ${uploadProgress.length} files`
                : uploadProgress.some((f) => f.status === 'failed')
                  ? `${uploadProgress.filter((f) => f.status === 'failed').length} of ${uploadProgress.length} files failed to upload`
                  : busy
                    ? 'Upload complete. Validation is still running, please wait.'
                    : `All ${uploadProgress.length} files uploaded successfully`}
            </p>
            {isUploading ? (
              <button
                onClick={onCancel}
                className="text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Cancel
              </button>
            ) : busy ? (
              /* Validation is still running. Show a spinner rather than a greyed-out
                 X: an X reads as "close" regardless of colour, so a disabled one
                 still invites a click that does nothing. A spinner says "wait". */
              <svg
                className="animate-spin w-5 h-5 text-gray-400"
                role="status"
                aria-label="Validating"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <button
                onClick={onDismiss}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Progress List */}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {uploadProgress.map((file, index) => (
              <div key={index} className="flex items-center gap-3">
                {/* Status Icon */}
                <div className="flex-shrink-0 w-5 h-5">
                  {file.status === 'pending' && (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                  )}
                  {file.status === 'uploading' && (
                    <svg
                      className="animate-spin w-5 h-5 text-blue-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  )}
                  {file.status === 'done' && (
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                  {file.status === 'failed' && (
                    <svg
                      className="w-5 h-5 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{file.filename}</p>
                  {file.status === 'uploading' && (
                    <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 transition-all duration-200"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  )}
                  {file.status === 'failed' && file.error && (
                    <p className="text-xs text-red-600 mt-0.5">{file.error}</p>
                  )}
                </div>

                {/* Progress Percentage */}
                {file.status === 'uploading' && (
                  <span className="text-sm text-gray-500 flex-shrink-0">{file.progress}%</span>
                )}
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            {uploadProgress.some((f) => f.status === 'failed') ? (
              <div className="space-y-3">
                <p className="text-sm text-red-600 font-medium">
                  {uploadProgress.filter((f) => f.status === 'failed').length} of{' '}
                  {uploadProgress.length} file(s) failed to upload
                </p>
                <p className="text-xs text-gray-500">
                  Please check the file requirements and try again. Files must match the expected
                  aspect ratio for the selected slot.
                </p>
                <button
                  onClick={onDismiss}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
                >
                  Dismiss
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                {uploadProgress.filter((f) => f.status === 'done').length} of{' '}
                {uploadProgress.length} files uploaded
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
